import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { apiFetch } from '@/integrations/api/client';
import { Loader2, Mail, ArrowLeft, MailCheck } from 'lucide-react';

const Logo = () => (
  <div className="flex items-center gap-2.5">
    <svg width="34" height="34" viewBox="0 0 64 64" fill="none"><rect width="64" height="64" rx="15" fill="#2563EB" /><rect x="12" y="42" width="8" height="16" rx="2" fill="white" opacity=".5" /><rect x="24" y="32" width="8" height="26" rx="2" fill="white" opacity=".7" /><rect x="36" y="22" width="8" height="36" rx="2" fill="white" /><rect x="48" y="12" width="8" height="46" rx="2" fill="#22C55E" /></svg>
    <span className="text-xl font-extrabold text-foreground">
      <span style={{ color: '#3b82f6' }}>Prodi</span>fy<span style={{ color: '#22c55e', fontSize: 13, verticalAlign: 'super' }}>360</span>
    </span>
  </div>
);

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await apiFetch('/api/auth/forgot-password', { method: 'POST', body: JSON.stringify({ email: email.trim().toLowerCase() }) });
    } catch { /* endpoint always returns ok; ignore network noise */ }
    finally { setLoading(false); setSent(true); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6 sm:p-10">
      <div className="w-full max-w-sm animate-fade-in">
        <div className="mb-8 flex justify-center"><Logo /></div>

        {sent ? (
          <div className="text-center">
            <div className="mx-auto mb-5 h-14 w-14 rounded-2xl bg-emerald-500/10 ring-1 ring-emerald-500/20 flex items-center justify-center">
              <MailCheck className="h-7 w-7 text-emerald-500" />
            </div>
            <h2 className="text-2xl font-bold tracking-tight">Check your email</h2>
            <p className="text-sm text-muted-foreground mt-2">
              If an account exists for <span className="font-medium text-foreground">{email.trim().toLowerCase()}</span>, we've sent password reset instructions. The link is valid for 1 hour.
            </p>
            <p className="text-xs text-muted-foreground/70 mt-4">Didn't get it? Check spam, or try again in a minute.</p>
            <a href="/login" className="mt-7 inline-flex items-center gap-1.5 text-sm text-primary font-semibold hover:underline">
              <ArrowLeft className="h-4 w-4" /> Back to sign in
            </a>
          </div>
        ) : (
          <>
            <div className="mb-7 text-center">
              <h2 className="text-2xl font-bold tracking-tight">Forgot password?</h2>
              <p className="text-sm text-muted-foreground mt-1">Enter your email and we'll send you a reset link.</p>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="fp-email" className="text-xs font-medium">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input id="fp-email" type="email" autoComplete="email" placeholder="you@company.com" className="pl-10 h-11"
                    value={email} onChange={(e) => setEmail(e.target.value)} disabled={loading} autoFocus />
                </div>
              </div>
              <Button type="submit" className="w-full h-11 text-[15px] font-semibold shadow-lg shadow-primary/20" disabled={loading}>
                {loading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Sending…</>) : 'Send Reset Link'}
              </Button>
            </form>
            <a href="/login" className="mt-6 flex items-center justify-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-4 w-4" /> Back to sign in
            </a>
          </>
        )}
      </div>
    </div>
  );
}
